import React, { useMemo, useState } from 'react';
import {
    Alert, Box, Button, Grid, LinearProgress, Menu, MenuItem, Skeleton, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import GroupIcon from '@mui/icons-material/Group';
import PageTitle from '../../components/PageTitle';
import KpiCard from './shared/KpiCard';
import FilterBar from './shared/FilterBar';
import { getDefaultDateRange, CLOSURE_CATEGORIES } from './shared/constants';
import useAppointmentDashboard from './shared/useAppointmentDashboard';
import { exportXlsx } from './shared/exportHelpers';
import InfoTip from './shared/InfoTip';

const { PROZO, NON_PROZO } = CLOSURE_CATEGORIES;

// Sample CSS replicas: `.chart-card` + `.bar-chart` + `.bar-fill` etc.
const chartCardSx = {
    bgcolor: '#fff',
    borderRadius: '10px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    height: '100%',
};
const chartH3Sx = { fontSize: '14px', fontWeight: 700, color: '#1a1a2e', mb: '4px' };
const chartSubSx = { fontSize: '11px', color: '#999', mb: '14px' };

// Every miss figure on this page counts MISSED APPOINTMENTS (history entries),
// not shipments — an AWB missed 3 times contributes 3. Shipment-level counts
// live on Missed & Repeat. Spelled out once here and reused in tooltips.
const MISS_UNIT_NOTE = 'Counts missed appointments (attempts), not shipments: an AWB missed 3 times counts 3. For shipment-level counts use Missed & Repeat.';

function ChartHeading({ title, sub, info }) {
    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: '4px' }}>
                <Typography component='h3' sx={{ ...chartH3Sx, mb: 0 }}>{title}</Typography>
                <InfoTip title={info} />
            </Box>
            <Typography sx={chartSubSx}>{sub}</Typography>
        </>
    );
}

// Sample `.bar-label` 200px, `.bar-track` 22px h + 4px radius, `.bar-fill`
// padding-left 8px + 10px font + min-width 30px.
function MissReasonBar({ row, max }) {
    const pct = max ? Math.round((row.count / max) * 100) : 0;
    const isProzo = row.category === PROZO;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box sx={{
                width: 200, textAlign: 'right', fontSize: '11px', color: '#555',
                overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                flexShrink: 0,
            }}>
                {row.label || row.code}
            </Box>
            <Box sx={{
                flex: 1, height: '22px', bgcolor: '#f0f0f0',
                borderRadius: '4px', overflow: 'hidden', position: 'relative',
            }}>
                <Box sx={{
                    width: `${pct}%`,
                    height: '100%',
                    minWidth: '30px',
                    borderRadius: '4px',
                    background: isProzo
                        ? 'linear-gradient(90deg, #e65100, #ff8f00)'
                        : 'linear-gradient(90deg, #1565c0, #42a5f5)',
                    display: 'flex',
                    alignItems: 'center',
                    pl: '8px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '10px',
                }}>
                    {row.count}
                </Box>
            </Box>
        </Box>
    );
}

// Sample `.donut-container` flex + 30px gap; `.legend-item` 12px text.
function AttributionDonut({ prozo, nonProzo, topProzo, topNonProzo, shipments }) {
    const total = (prozo || 0) + (nonProzo || 0);
    const prozoPct = total ? Math.round((prozo / total) * 100) : 0;
    const nonPct = total ? 100 - prozoPct : 0;
    const circumference = 2 * Math.PI * 70;
    const prozoLen = (prozoPct / 100) * circumference;
    const nonLen = circumference - prozoLen;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '30px', pt: 2 }}>
            <svg width='180' height='180' viewBox='0 0 180 180'>
                <circle cx='90' cy='90' r='70' fill='none' stroke='#e0e0e0' strokeWidth='28' />
                <circle cx='90' cy='90' r='70' fill='none' stroke='#1565c0' strokeWidth='28'
                    strokeDasharray={`${nonLen} ${prozoLen}`} transform='rotate(-90 90 90)' />
                <circle cx='90' cy='90' r='70' fill='none' stroke='#e65100' strokeWidth='28'
                    strokeDasharray={`${prozoLen} ${nonLen}`} strokeDashoffset={`-${nonLen}`} transform='rotate(-90 90 90)' />
                <text x='90' y='82' textAnchor='middle' fontSize='22' fontWeight='800' fill='#333'>{total}</text>
                <text x='90' y='97' textAnchor='middle' fontSize='9' fill='#888'>Missed Appointments</text>
                {shipments != null && (
                    <text x='90' y='110' textAnchor='middle' fontSize='9' fill='#aaa'>across {shipments} shipments</text>
                )}
            </svg>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#555' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#1565c0', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '12px' }}><strong>Non-Prozo:</strong> {nonProzo} ({nonPct}%)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#555' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#e65100', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '12px' }}><strong>Prozo:</strong> {prozo} ({prozoPct}%)</Typography>
                </Box>
                <Box sx={{ borderTop: '1px solid #eee', pt: '12px', mt: '4px' }}>
                    <Typography sx={{ fontSize: '11px', color: '#999' }}>Top Prozo reason:</Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{topProzo?.label || '—'}{topProzo?.count != null ? ` (${topProzo.count})` : ''}</Typography>
                    <Typography sx={{ fontSize: '11px', color: '#999', mt: 1 }}>Top Non-Prozo reason:</Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{topNonProzo?.label || '—'}{topNonProzo?.count != null ? ` (${topNonProzo.count})` : ''}</Typography>
                </Box>
            </Box>
        </Box>
    );
}

// Placeholder bars for the miss-reason chart while the dashboard request is pending.
function ReasonBarsSkeleton() {
    return [0, 1, 2, 3, 4].map((i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Skeleton variant='text' width={200} sx={{ flexShrink: 0 }} />
            <Skeleton variant='rounded' height={22} sx={{ flex: 1 }} width={`${90 - i * 15}%`} />
        </Box>
    ));
}

function TableRowsSkeleton({ cols, rows = 5 }) {
    return Array.from({ length: rows }).map((_, r) => (
        <TableRow key={r}>
            {Array.from({ length: cols }).map((__, c) => (
                <TableCell key={c} sx={perfTdSx}><Skeleton variant='text' /></TableCell>
            ))}
        </TableRow>
    ));
}

// Empty-body row for the perf tables: "No data" only once a request has actually succeeded.
function TableStatusRow({ cols, failed }) {
    return (
        <TableRow>
            <TableCell colSpan={cols} align='center' sx={{ color: '#888', py: 2, fontSize: '12px' }}>
                {failed ? 'Could not load data' : 'No data'}
            </TableCell>
        </TableRow>
    );
}

export default function ReportsAnalytics() {
    const def = getDefaultDateRange();
    const [filters, setFilters] = useState({ fromDate: def.fromDate, toDate: def.toDate });
    const [menuAnchor, setMenuAnchor] = useState(null);

    const { data: dash, loading, error, refetch } = useAppointmentDashboard({
        filters,
        include: ['buckets', 'summary', 'perf', 'reasons'],
        groupBy: ['courier', 'merchant'],
    });
    const summary = dash;
    // The hook swallows failures into EMPTY_STATE; surface them instead of rendering zeros as real data.
    const failed = !loading && !!error;
    const errorMsg = error ? (error?.response?.data?.message || error.message || 'Something went wrong') : '';
    const kpi = (v) => (failed ? '—' : v);
    const reasons = useMemo(() => dash?.reasons || [], [dash]);
    const courier = useMemo(() => dash?.perf?.courier || [], [dash]);
    const merchant = useMemo(() => dash?.perf?.merchant || [], [dash]);

    const maxCount = reasons.reduce((m, r) => Math.max(m, r.count || 0), 0);
    const sortedReasons = useMemo(() => [...reasons].sort((a, b) => (b.count || 0) - (a.count || 0)).slice(0, 10), [reasons]);

    const attribution = useMemo(() => {
        const prozo = reasons.filter((r) => r.category === PROZO).reduce((s, r) => s + (r.count || 0), 0);
        const nonProzo = reasons.filter((r) => r.category === NON_PROZO).reduce((s, r) => s + (r.count || 0), 0);
        const topProzo = reasons.filter((r) => r.category === PROZO).sort((a, b) => (b.count || 0) - (a.count || 0))[0];
        const topNonProzo = reasons.filter((r) => r.category === NON_PROZO).sort((a, b) => (b.count || 0) - (a.count || 0))[0];
        return { prozo, nonProzo, topProzo, topNonProzo };
    }, [reasons]);

    const reasonsSheet = useMemo(() => ({
        name: 'Miss Reasons',
        rows: reasons.map((r) => ({ Code: r.code, Label: r.label || r.code, Category: r.category, 'Missed Appointments': r.count || 0 })),
    }), [reasons]);
    const attributionSheet = useMemo(() => {
        const total = (attribution.prozo || 0) + (attribution.nonProzo || 0);
        return {
            name: 'Attribution',
            rows: [
                { Bucket: 'Prozo Attributable', 'Missed Appointments': attribution.prozo || 0, Percent: total ? Math.round((attribution.prozo / total) * 100) : 0, TopReason: attribution.topProzo?.label || '' },
                { Bucket: 'Non-Prozo Attributable', 'Missed Appointments': attribution.nonProzo || 0, Percent: total ? Math.round((attribution.nonProzo / total) * 100) : 0, TopReason: attribution.topNonProzo?.label || '' },
            ],
        };
    }, [attribution]);
    const courierSheet = useMemo(() => ({
        name: 'Courier Performance',
        rows: courier.map((c) => ({
            Courier: c.label,
            Total: c.total || 0,
            Delivered: c.delivered || 0,
            'Missed (Prozo)': c.missedProzo || 0,
            'Missed (Non-Prozo)': c.missedNonProzo || 0,
            'Delivery Rate %': c.deliveryRate || 0,
        })),
    }), [courier]);
    const merchantSheet = useMemo(() => ({
        name: 'Merchant Performance',
        rows: merchant.map((m) => ({
            Merchant: m.label,
            Total: m.total || 0,
            'Avg Attempts': m.avgAttempts ? Number(m.avgAttempts).toFixed(1) : '',
            'Prozo Fault Rate %': m.prozoFaultRate || 0,
        })),
    }), [merchant]);

    const downloadSingle = (sheet, baseName) => { exportXlsx(baseName, [sheet]); setMenuAnchor(null); };
    const downloadAll = () => { exportXlsx('ABD_Analytics_AllReports', [reasonsSheet, attributionSheet, courierSheet, merchantSheet]); setMenuAnchor(null); };

    return (
        <>
            <PageTitle>Reports & Analytics</PageTitle>
            {loading && <LinearProgress sx={{ mb: 1 }} />}

            {/* Header row — title left, download dropdown right (sample lines 879-917) */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                    <Typography component='h2' sx={{ fontSize: '16px', fontWeight: 700, color: '#1a1a2e', mb: '2px' }}>
                        Reports &amp; Analytics — How Are Our Appointments Performing?
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#888' }}>
                        Performance overview for the selected date range
                    </Typography>
                </Box>
                <Box sx={{ position: 'relative' }}>
                    <Button
                        variant='outlined' size='small'
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                        onClick={(e) => setMenuAnchor(e.currentTarget)}
                        disabled={loading || failed}
                        sx={{
                            fontSize: '12px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '7px 14px',
                            color: '#1565c0',
                            borderColor: '#1565c0',
                            bgcolor: 'transparent',
                            borderRadius: '4px',
                            '&:hover': { borderColor: '#1565c0', bgcolor: '#e3f2fd' },
                        }}
                    >Download Reports ▾</Button>
                    <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={() => setMenuAnchor(null)}
                        PaperProps={{ sx: { mt: 0.5, minWidth: 280, borderRadius: '8px' } }}
                    >
                        <Box sx={{
                            px: '14px', py: '10px',
                            borderBottom: '1px solid #f0f0f0',
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#888',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        }}>Download Analytics Reports</Box>
                        <MenuItem
                            onClick={() => downloadSingle(reasonsSheet, 'ABD_MissReasonBreakdown')}
                            sx={{ px: '14px', py: '10px', gap: '10px' }}
                        >
                            <BarChartIcon sx={{ fontSize: 16, color: '#1565c0' }} />
                            <Box>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>Miss Reason Breakdown</Typography>
                                <Typography sx={{ fontSize: '10px', color: '#999' }}>All miss reasons with counts and attribution</Typography>
                            </Box>
                        </MenuItem>
                        <MenuItem
                            onClick={() => downloadSingle(attributionSheet, 'ABD_ProzoVsNonProzo')}
                            sx={{ px: '14px', py: '10px', gap: '10px' }}
                        >
                            <PieChartIcon sx={{ fontSize: 16, color: '#e65100' }} />
                            <Box>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>Prozo vs Non-Prozo Attribution</Typography>
                                <Typography sx={{ fontSize: '10px', color: '#999' }}>Attribution split with detailed breakdown</Typography>
                            </Box>
                        </MenuItem>
                        <MenuItem
                            onClick={() => downloadSingle(courierSheet, 'ABD_CourierPerformance')}
                            sx={{ px: '14px', py: '10px', gap: '10px' }}
                        >
                            <TimelineIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                            <Box>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>Courier-wise Performance</Typography>
                                <Typography sx={{ fontSize: '10px', color: '#999' }}>Delivery rates, miss counts per courier</Typography>
                            </Box>
                        </MenuItem>
                        <MenuItem
                            onClick={() => downloadSingle(merchantSheet, 'ABD_MerchantPerformance')}
                            sx={{ px: '14px', py: '10px', gap: '10px' }}
                        >
                            <GroupIcon sx={{ fontSize: 16, color: '#7b1fa2' }} />
                            <Box>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>Merchant-wise Performance</Typography>
                                <Typography sx={{ fontSize: '10px', color: '#999' }}>Avg attempts, miss rates per merchant</Typography>
                            </Box>
                        </MenuItem>
                        <Box sx={{ px: '14px', py: '10px', borderTop: '1px solid #f0f0f0' }}>
                            <Button
                                fullWidth size='small'
                                onClick={downloadAll}
                                sx={{
                                    bgcolor: '#1565c0',
                                    color: '#fff',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    padding: '5px 12px',
                                    borderRadius: '4px',
                                    '&:hover': { bgcolor: '#0d47a1' },
                                }}
                            >Download All Reports (Excel)</Button>
                        </Box>
                    </Menu>
                </Box>
            </Box>

            <FilterBar
                show={['merchant', 'courier', 'dateRange']}
                onApply={(f) => setFilters(f)}
                onReset={() => setFilters({ fromDate: def.fromDate, toDate: def.toDate })}
            />

            {failed && (
                <Alert severity='error' sx={{ mb: 2 }}
                    action={<Button color='inherit' size='small' onClick={() => refetch()}>Retry</Button>}>
                    Could not load appointment analytics: {errorMsg}
                </Alert>
            )}

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                    <KpiCard color='blue' label='Total Appointments' value={kpi(summary.totalAppts)} loading={loading}
                        sub='Created in range' icon={<EventNoteIcon />}
                        info='ABD shipments whose AWB was registered in the selected date range (one per AWB), regardless of current status.' />
                </Grid>
                <Grid item xs={12} md={3}>
                    <KpiCard color='green' label='First-Attempt Delivery Rate' value={kpi(`${summary.firstAttemptRate ?? 0}%`)} loading={loading}
                        sub='Delivered on Appointment #1' icon={<CheckCircleIcon />}
                        info='Of delivered shipments in range, the share delivered with zero missed appointments.' />
                </Grid>
                <Grid item xs={12} md={3}>
                    <KpiCard color='amber' label='Avg Attempts per Delivery' value={kpi(summary.avgAttempts ?? 0)} loading={loading}
                        sub='Across delivered shipments' icon={<TrendingUpIcon />}
                        info='Average of (missed appointments + 1) over delivered shipments in range.' />
                </Grid>
                <Grid item xs={12} md={3}>
                    <KpiCard color='red' label='Prozo Attributable Miss Rate' value={kpi(`${summary.prozoMissRate ?? 0}%`)} loading={loading}
                        sub='Of all missed appointments' icon={<ReportProblemIcon />}
                        info={`Prozo-attributed missed appointments ÷ all missed appointments in range. ${MISS_UNIT_NOTE}`} />
                </Grid>
            </Grid>

            {/* Charts row — sample `.analytics-row`: grid 1fr 1fr, gap 18px */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '18px', mb: '18px' }}>
                <Box sx={chartCardSx}>
                    <ChartHeading
                        title='Miss Reason Breakdown'
                        sub='Top 10 reasons by missed appointments in selected range'
                        info={`Shows the top 10 reasons only, so the bars need not add up to the attribution total. Download Reports → Miss Reason Breakdown has the full list. ${MISS_UNIT_NOTE}`}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {loading && <ReasonBarsSkeleton />}
                        {!loading && sortedReasons.length === 0 && (
                            <Typography sx={{ fontSize: '11px', color: '#999' }}>{failed ? 'Could not load data' : 'No data'}</Typography>
                        )}
                        {!loading && sortedReasons.map((r) => (
                            <MissReasonBar key={r.code} row={r} max={maxCount} />
                        ))}
                    </Box>
                </Box>
                <Box sx={chartCardSx}>
                    <ChartHeading
                        title='Prozo vs Non-Prozo Attribution'
                        sub='Who was responsible for missed appointments?'
                        info={`Total in the centre is the number of missed appointments in range across all reasons. ${MISS_UNIT_NOTE} The shipment count below it is the number of distinct AWBs with at least one miss (same as "All Missed" on Missed & Repeat).`}
                    />
                    {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '30px', pt: 2 }}>
                            <Skeleton variant='circular' width={180} height={180} />
                            <Box sx={{ width: 160 }}>
                                <Skeleton variant='text' /><Skeleton variant='text' /><Skeleton variant='text' width='60%' />
                            </Box>
                        </Box>
                    ) : failed ? (
                        <Typography sx={{ fontSize: '11px', color: '#999' }}>Could not load data</Typography>
                    ) : (
                        <AttributionDonut
                            prozo={attribution.prozo}
                            nonProzo={attribution.nonProzo}
                            topProzo={attribution.topProzo}
                            topNonProzo={attribution.topNonProzo}
                            shipments={dash.missedAny}
                        />
                    )}
                </Box>
            </Box>

            {/* Courier-wise performance — sample table styling */}
            <Box sx={{ ...chartCardSx, mb: '18px' }}>
                <ChartHeading
                    title='Courier-wise Appointment Performance'
                    sub='Delivery success rate by courier partner'
                    info={`Total Appts and Delivered are shipments. Missed (Prozo) / Missed (Non-Prozo) are missed appointments. ${MISS_UNIT_NOTE}`}
                />
                <TableContainer sx={{ mt: '12px' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#fafafa' }}>
                                <TableCell sx={perfThSx}>Courier Partner</TableCell>
                                <TableCell sx={{ ...perfThSx, textAlign: 'center' }} align='center'>Total Appts</TableCell>
                                <TableCell sx={{ ...perfThSx, textAlign: 'center' }} align='center'>Delivered</TableCell>
                                <TableCell sx={{ ...perfThSx, textAlign: 'center' }} align='center'>Missed (Prozo)</TableCell>
                                <TableCell sx={{ ...perfThSx, textAlign: 'center' }} align='center'>Missed (Non-Prozo)</TableCell>
                                <TableCell sx={{ ...perfThSx, textAlign: 'center' }} align='center'>Delivery Rate</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && <TableRowsSkeleton cols={6} />}
                            {!loading && courier.length === 0 && <TableStatusRow cols={6} failed={failed} />}
                            {!loading && courier.map((c) => (
                                <TableRow key={c.key || c.label} hover>
                                    <TableCell sx={perfTdSx}>
                                        {c.label}
                                        {c.key && <Typography component='span' sx={{ fontSize: '11px', color: '#888', ml: 0.5 }}>({c.key})</Typography>}
                                    </TableCell>
                                    <TableCell sx={perfTdSx} align='center'>{c.total}</TableCell>
                                    <TableCell sx={perfTdSx} align='center'>{c.delivered}</TableCell>
                                    <TableCell sx={perfTdSx} align='center'>{c.missedProzo}</TableCell>
                                    <TableCell sx={perfTdSx} align='center'>{c.missedNonProzo}</TableCell>
                                    <TableCell sx={perfTdSx} align='center'>
                                        <Typography component='span' sx={{
                                            fontWeight: 700,
                                            fontSize: '12px',
                                            color: (c.deliveryRate || 0) >= 70 ? '#2e7d32' : '#e65100',
                                        }}>{c.deliveryRate || 0}%</Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Merchant-wise performance */}
            <Box sx={chartCardSx}>
                <ChartHeading
                    title='Merchant-wise Appointment Performance'
                    sub='Avg attempts and Prozo fault rate per merchant'
                    info={`Total Appts and Delivered are shipments. Missed (Prozo) / Missed (Non-Prozo) are missed appointments. ${MISS_UNIT_NOTE}`}
                />
                <TableContainer sx={{ mt: '12px' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#fafafa' }}>
                                <TableCell sx={perfThSx}>Merchant</TableCell>
                                <TableCell sx={{ ...perfThSx, textAlign: 'center' }} align='center'>Total</TableCell>
                                <TableCell sx={{ ...perfThSx, textAlign: 'center' }} align='center'>Avg Attempts</TableCell>
                                <TableCell sx={{ ...perfThSx, textAlign: 'center' }} align='center'>Prozo Fault Rate</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && <TableRowsSkeleton cols={4} />}
                            {!loading && merchant.length === 0 && <TableStatusRow cols={4} failed={failed} />}
                            {!loading && merchant.map((m) => (
                                <TableRow key={m.key || m.label} hover>
                                    <TableCell sx={perfTdSx}>{m.label}</TableCell>
                                    <TableCell sx={perfTdSx} align='center'>{m.total}</TableCell>
                                    <TableCell sx={perfTdSx} align='center'>
                                        {m.avgAttempts ? Number(m.avgAttempts).toFixed(1) : '—'}
                                    </TableCell>
                                    <TableCell sx={perfTdSx} align='center'>{m.prozoFaultRate || 0}%</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    );
}

// Sample `.data-table thead th` / `tbody td` — 11px header uppercase / 12px body.
const perfThSx = {
    py: '10px',
    px: '10px',
    fontSize: '11px',
    fontWeight: 700,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    bgcolor: '#fafafa',
    borderBottom: '2px solid #e0e0e0',
};
const perfTdSx = {
    py: '10px',
    px: '10px',
    fontSize: '12px',
    color: '#333',
    borderBottom: '1px solid #f0f0f0',
};
